import { useState, useMemo } from "react";
import TabPanel, { Item } from "devextreme-react/tab-panel";
import { Button, Popup, ScrollView } from "devextreme-react";
import UniqueCustomersGrid from "./UniqueCustomersGrid";
import CustomersChart from "./CustomersChart";
import "./UniqueCustomerModal.css";

import InfoPeriodsChart from "../InfoPeriods/InfoPeriodsChart";
import InfoCurrentPeriodsGrid from "../InfoPeriods/InfoCurrentPeriodsGrid";
import { useSelector } from "react-redux";
import { LinearProgress } from "@mui/material";
import { fetcher } from "../../../../actions";
import useSWR from "swr";
import PropTypes from 'prop-types';
import ExcludedItemsTab from "./ExcludedItemsTab";
import { TopBuyerShareChart } from "./TopBuyerShareChart";
import { PeriodButtons } from "./PeriodButton";


export const theme = {
  colors: {
    primary: '#0078D4', // Azure blue
    secondary: '#50E6FF',
    background: '#FFFFFF',
    surface: '#F8F9FA',
    error: '#D83B01',
    success: '#107C10',
    text: {
      primary: '#323130',
      secondary: '#605E5C'
    },
    button: {
      primary: '#0078D4',
      hover: '#106EBE',
      selected: '#56c0f5'
    }
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px'
  },
  shadows: {
    modal: '0 4px 24px rgba(0, 0, 0, 0.15)',
    card: '0 2px 8px rgba(0, 0, 0, 0.1)'
  }
};




export const UniqueCustomerModal = (props) => {
  const currentPermissions = useSelector((state) => state.userInfo.permissions);
  const { open, onClose, name, internalId, Period, tab } = props;
  const [newPeriod, setNewPeriod] = useState(Period);
  const [tabIndex, setTabIndex] = useState(tab);

  const {
    data: dataUniqueCustomers,
  } = useSWR(
    `/api/Report/reportuniquecustomer?internalid=${internalId}`,
    fetcher
  );

  const {
    data: dataSourcePeriods,
    isLoading: isLoadingPeriods,
    error: errorPeriods,
  } = useSWR(
    `/api/Report/reportInfoPeriod?InternalID=${internalId}&Period=${newPeriod}`,
    fetcher
  );

  const {
    data: dataExcludedItems,
    isLoading: isLoadingExcludedItems,
    error: errorExcludedItems,
  } = useSWR(
    `/api/ExcludedItemFulfillment/${internalId}`,
    fetcher
  );


  const visibleAuthorization = (periodButton) => {
    var visible = "none";
    let column = currentPermissions.filter(
      (x) => x.NameColumn === periodButton
    )[0];
    if (column) {
      visible = "";
    }
    return visible;
  };


  // AFTER (Memoized calculations)
  const customerMetrics = useMemo(() => {
    if (!dataUniqueCustomers || dataUniqueCustomers.length === 0) {
      return { topBuyerShare: 0, totalUSCustomers: 0, totalIntCustomers: 0, totalCustomers: 0 };
    }

    const firstCustomer = dataUniqueCustomers[0];
    const totalUSCustomers = firstCustomer.US_cust || 0;
    const totalIntCustomers = firstCustomer.Intl_cust || 0;

    return {
      topBuyerShare: (firstCustomer.TopBuyerShare || 0) * 100,
      totalUSCustomers,
      totalIntCustomers,
      totalCustomers: totalUSCustomers + totalIntCustomers
    };
  }, [dataUniqueCustomers]);


  const onChangePeriod = (newPeriod) => {
    setNewPeriod(newPeriod);
  };

  const YearLabel = (year) => {
    //Current date utc
    let myDate = new Date();
    //Convert to PST Time
    let pstDate = myDate.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
    });
    //Extract the year
    let yearTime = pstDate.split("/");
    let yearPST = yearTime[2].split(",");
    let yearl = yearPST[0] - year;
    let yeard = yearl;
    //Return the current year minus the value sent
    return "(Y" + year + ") " + yeard;
  };

  const onSelectionChanged = (args) => {
    if (!args.addedItems[0]) {
      return;
    }
    const selectedIndex = args.addedItems[0].selectedIndex;
    const tabIndex = selectedIndex === "0" ? 0 : selectedIndex === "1" ? 1 : 2;
    setTabIndex(tabIndex);
  };

  const PERIOD_TYPES = {
    L3M: { id: 1, code: 'L3M', name: 'L3M', auth: null, label: 'Last 3 Months' },
    L12M: { id: 2, code: 'L12M', name: 'L12M', auth: null, label: 'Last 12 Months' },
    Y0: { id: 3, code: 'Y0', name: 'y0', auth: 'Y0', label: 'Current Year' },
    Y1: { id: 4, code: 'Y1', name: 'y1', auth: 'Y1', label: 'Last Year' },
    Y2: { id: 5, code: 'Y2', name: 'y2', auth: 'Y2', label: '2 Years Ago' },
    Y3: { id: 6, code: 'Y3', name: 'y3', auth: 'Y3', label: '3 Years Ago' },
    YOY: { id: 7, code: 'YOY', name: 'yt', auth: 'Y0', label: 'Year over Year' }
  };

  const getPeriodById = (id) => {
    return Object.values(PERIOD_TYPES).find(period => period.id === id) || null;
  };

  const renderPeriodGrid = (internalId, newPeriod, isCurrentMonth, title) => {
    const period = getPeriodById(newPeriod);
    const periodCode = period ? period.code : null;
    return (
      <>
        <InfoCurrentPeriodsGrid internalId={internalId} period={periodCode} currentMonth={isCurrentMonth} title={title} />
      </>
    );
  }

  const renderModalContent = () => (
    <TabPanel
      selectedIndex={tabIndex}
      onSelectionChanged={onSelectionChanged}
      className="tab-panel"
      height={"90%"}
    >
      <Item title="Unique Customers (L12M)" id="customer" selectedIndex="0">
        <ScrollView height="100%" width="100%">
          <div className="mainContainer">
            <div className="analytics-section">
              <div className="left-panel">
                <div className="metrics-container">
                      <CustomersChart
                        data={dataUniqueCustomers}
                        totalCustomers={customerMetrics.totalCustomers}
                      />
                  <div className="gauge-container">
                    <TopBuyerShareChart
                      topBuyerShare={customerMetrics.topBuyerShare}
                    />
                  </div>
                </div>
              </div>
              <div className="right-panel">
                <div className="gridContainer">
                  <UniqueCustomersGrid dataSource={dataUniqueCustomers} />
                </div>

              </div>
            </div>

          </div>
        </ScrollView>
      </Item>

      <Item title="Fulfillment History" id="Periods" selectedIndex="1">
        <ScrollView height="100%" width="100%">
          <div className="period-section">
            <div className="period-buttons-container">
              <PeriodButtons
                selectedPeriod={newPeriod}
                onPeriodChange={onChangePeriod}
                checkAuthorization={visibleAuthorization}
                getYearLabel={YearLabel}
              />
            </div>
            {errorPeriods ? (
              <div className="error-container">
                <span className="error-icon">⚠️</span>
                <span>Failed to load period data</span>
              </div>
            ) : isLoadingPeriods ? (
              <div className="loading-container">
                <LinearProgress className="loading-spinner" />
                <span>Loading period data...</span>
              </div>
            ) : (
              <div className="period-content">
                <InfoPeriodsChart dataSourcePeriods={dataSourcePeriods} />
                <div className="period-grids">
                  {renderPeriodGrid(internalId, newPeriod, false, "Period Transactions")}
                  {renderPeriodGrid(internalId, newPeriod, true, "Current Month Transactions")}
                </div>
              </div>
            )}
          </div>
        </ScrollView>
      </Item>
      {(tabIndex === 2 || dataExcludedItems?.totalExcludedItems > 0) && (
        <Item title="Exclusions" id="Exclusions" selectedIndex="2">
          {errorExcludedItems ? (
            <div className="error-container">
              <span className="error-icon">⚠️</span>
              <span>Failed to load exclusions data</span>
            </div>
          ) : isLoadingExcludedItems ? (
            <div className="loading-container">
              <LinearProgress className="loading-spinner" />
              <span>Loading exclusions data...</span>
            </div>
          ) : (
            <ExcludedItemsTab
              data={dataExcludedItems}
              internalId={getPeriodById(newPeriod)}
            />
          )}
        </Item>
      )}
    </TabPanel>
  );

  return (
    <Popup
      id="popup"
      visible={open}
      onHiding={() => onClose("uniqueCustomer", false)}
      showTitle={true}
      titleRender={() => (
        <div className="modal-header">
          <h3 className="modal-title">{name} - Sales Review</h3>
          <Button
            className="modal-close"
            onClick={() => onClose("uniqueCustomer", false)}
          >
            ✕
          </Button>
        </div>
      )}
      resizeEnabled={true}
      dragEnabled={true}
      className="modal-container"
      width="90vw"
      maxWidth="1400px"
      minWidth="800px"
      height="85vh"
      maxHeight="900px"
    >
      {renderModalContent()}
    </Popup>
  );
};


UniqueCustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  internalId: PropTypes.string.isRequired,
  Period: PropTypes.number.isRequired,
  tab: PropTypes.number.isRequired
};
export default UniqueCustomerModal;
